import React, { useEffect, useState } from "react";
import { RefreshCw, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const CHECK_INTERVAL_MS = 3 * 60 * 1000; // Check every 3 minutes

export default function VersionUpdateChecker() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const currentBuildTime = import.meta.env.VITE_BUILD_TIME;

  useEffect(() => {
    // Provide a helper to test or re-trigger the banner at any time in the browser console: window.__showUpdateBanner()
    window.__showUpdateBanner = () => {
      setDismissed(false);
      setUpdateAvailable(true);
    };

    // Only run periodic background checks in production when build time exists
    if (!currentBuildTime || import.meta.env.DEV) {
      return;
    }

    const checkVersion = async () => {
      try {
        const response = await fetch(`/version.json?_t=${Date.now()}`, {
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });

        if (!response.ok) return;

        const data = await response.json();
        if (data?.buildTime && String(data.buildTime) !== String(currentBuildTime)) {
          setUpdateAvailable(true);
        }
      } catch {
        // Silently catch network errors during background check
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };

    checkVersion();
    const interval = setInterval(checkVersion, CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [currentBuildTime]);

  if (!updateAvailable || dismissed) {
    return null;
  }

  const handleUpdate = () => {
    window.location.reload();
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] max-w-lg w-[calc(100%-2rem)] sm:w-auto animate-in fade-in slide-in-from-top-4 duration-300 pointer-events-auto"
    >
      <div className="flex items-center gap-3.5 px-4 py-3 bg-card text-card-foreground rounded-xl border border-border shadow-2xl backdrop-blur-md">
        <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0">
          <Sparkles className="w-4 h-4 animate-pulse" />
        </div>

        <div className="flex flex-col min-w-0 pr-1">
          <div className="flex items-center gap-1.5">
            <span className="text-xs sm:text-sm font-semibold text-foreground">
              New Version Available
            </span>
          </div>
          <span className="text-xs text-muted-foreground truncate">
            An update for Easy Retail Flow is ready.
          </span>
        </div>

        <div className="flex items-center gap-2 ml-auto shrink-0">
          <Button
            size="sm"
            onClick={handleUpdate}
            className="gap-1.5 h-8 px-3 text-xs font-medium shadow-sm cursor-pointer"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Update Now
          </Button>

          <Button
            size="icon"
            variant="ghost"
            onClick={() => setDismissed(true)}
            className="h-8 w-8 text-muted-foreground hover:text-foreground cursor-pointer"
            title="Dismiss"
            aria-label="Dismiss update notification"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
